/**
 * ConfirmDialog — reemplazo Apple/glass de window.confirm().
 * Se controla desde el store: askConfirm({ title, message, confirmText, cancelText, danger })
 */
import React, { useEffect } from 'react';
import { AlertTriangle } from 'lucide-react';
import useStore from '../store/useStore';

export default function ConfirmDialog() {
  const confirmState = useStore(s => s.confirmState);
  const resolveConfirm = useStore(s => s.resolveConfirm);

  useEffect(() => {
    if (!confirmState) return;
    const onKey = (e) => {
      if (e.key === 'Escape') resolveConfirm(false);
      if (e.key === 'Enter') resolveConfirm(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [confirmState, resolveConfirm]);

  if (!confirmState) return null;

  const {
    title = '¿Confirmar?',
    message = '',
    confirmText = 'Confirmar',
    cancelText = 'Cancelar',
    danger = false,
  } = confirmState;

  const accent = danger ? '#FF3B30' : '#007AFF';

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4 animate-fade-in"
      style={{ background: 'rgba(0,0,0,0.28)', WebkitBackdropFilter: 'blur(4px)', backdropFilter: 'blur(4px)' }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) resolveConfirm(false); }}
    >
      <div
        className="w-full max-w-sm rounded-2xl p-6 text-center"
        style={{
          background: 'rgba(255,255,255,0.94)',
          WebkitBackdropFilter: 'saturate(180%) blur(40px)',
          backdropFilter: 'saturate(180%) blur(40px)',
          border: '1px solid rgba(255,255,255,0.75)',
          boxShadow: '0 20px 50px rgba(0,0,0,.18), 0 4px 12px rgba(0,0,0,.08)',
        }}
      >
        <div
          className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-4"
          style={{ background: accent + '15' }}
        >
          <AlertTriangle size={22} style={{ color: accent }} strokeWidth={2} />
        </div>
        <h3 className="text-base font-bold text-n-800 tracking-apple-tight">{title}</h3>
        {message && <p className="text-sm text-n-500 mt-1.5 leading-snug">{message}</p>}
        <div className="flex gap-3 mt-6">
          <button
            onClick={() => resolveConfirm(false)}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-n-700 transition-all hover:opacity-80"
            style={{ background: 'rgba(0,0,0,0.05)' }}
          >
            {cancelText}
          </button>
          <button
            onClick={() => resolveConfirm(true)}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 shadow-apple-sm"
            style={{ background: accent }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
